import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { usePlaybackUrl, type VideoPlaybackInfo } from '../../hooks/usePlaybackUrl';
import LibraryVideoPlayer from './LibraryVideoPlayer';
import { extractExternalVideoEmbedUrl } from './propertyCardUtils';

interface PropertyCardVideoEmbedProps {
  videoSource?: 'external' | 'library';
  videoURL?: string;
  libraryVideoExternalId?: string;
  getVideoPlaybackUrl?: (externalId: string) => Promise<VideoPlaybackInfo>;
  /** The card's own title, when it has one — it wins over the video's user-facing title. */
  cardTitle?: string;
}

const frameSx = {
  position: 'relative' as const,
  width: '100%',
  aspectRatio: '16/9',
  backgroundColor: '#000',
  zIndex: 1,
};

function LibraryVideoEmbed({
  externalId,
  getVideoPlaybackUrl,
  cardTitle,
}: {
  externalId: string;
  getVideoPlaybackUrl: (externalId: string) => Promise<VideoPlaybackInfo>;
  cardTitle?: string;
}) {
  const { url, title, description, loading, error } = usePlaybackUrl(
    externalId,
    getVideoPlaybackUrl,
  );

  if (error) {
    return (
      <Box
        sx={{
          ...frameSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '1rem',
        }}
      >
        <Typography variant="caption" sx={{ color: '#fff' }}>
          Video unavailable
        </Typography>
      </Box>
    );
  }

  if (loading || !url) {
    return (
      <Box
        sx={{
          ...frameSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '1rem',
        }}
      >
        <CircularProgress size={28} sx={{ color: '#fff' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ ...frameSx, borderRadius: '1rem' }}>
      <LibraryVideoPlayer
        url={url}
        title={cardTitle || title}
        description={description}
        videoStyle={{ borderRadius: '1rem', objectFit: 'contain', backgroundColor: '#000' }}
      />
    </Box>
  );
}

export default function PropertyCardVideoEmbed({
  videoSource = 'external',
  videoURL,
  libraryVideoExternalId,
  getVideoPlaybackUrl,
  cardTitle,
}: PropertyCardVideoEmbedProps) {
  if (videoSource === 'library' && libraryVideoExternalId && getVideoPlaybackUrl) {
    return (
      <LibraryVideoEmbed
        externalId={libraryVideoExternalId}
        getVideoPlaybackUrl={getVideoPlaybackUrl}
        cardTitle={cardTitle}
      />
    );
  }

  return (
    <Box sx={frameSx}>
      <iframe
        width="100%"
        height="100%"
        src={extractExternalVideoEmbedUrl(videoURL || '')}
        title="Embedded Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ border: 'none', borderRadius: '1rem' }}
      />
    </Box>
  );
}
